import { useEffect, useState } from 'react';
import { LifeBuoy, MessageSquare, SendHorizonal } from 'lucide-react';
import { supportService } from '../services/supportService';

interface SupportThread {
  id: string;
  driverName: string;
  driverPhone: string;
  subject: string;
  status: string;
  lastMessage: string;
  lastMessageAt: string;
}

interface SupportMessage {
  id: string;
  senderRole: string;
  senderName: string;
  message: string;
  createdAt: string;
}

export function SupportCenter() {
  const [threads, setThreads] = useState<SupportThread[]>([]);
  const [selectedThread, setSelectedThread] = useState<SupportThread | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState<'unanswered' | 'answered'>('unanswered');

  useEffect(() => {
    loadThreads();
  }, []);

  const loadThreads = async () => {
    try {
      setLoading(true);
      const response = await supportService.getThreads();
      const rows = (response.data || response) as SupportThread[];
      setThreads(rows || []);
      if (!selectedThread && rows?.length) {
        await selectThread(rows[0]);
      }
    } catch (error) {
      console.error('Support threads load failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectThread = async (thread: SupportThread) => {
    setSelectedThread(thread);
    try {
      const response = await supportService.getThread(thread.id);
      const payload = response.data || response;
      setMessages(payload?.messages || []);
    } catch (error) {
      console.error('Support thread detail load failed:', error);
      setMessages([]);
    }
  };

  const handleReply = async () => {
    if (!selectedThread || !draft.trim()) return;
    try {
      setSending(true);
      await supportService.sendThreadMessage(selectedThread.id, { message: draft.trim(), status: 'answered' });
      setDraft('');
      await selectThread(selectedThread);
      await loadThreads();
    } catch (error) {
      console.error('Support reply failed:', error);
      alert('Yanit gonderilemedi');
    } finally {
      setSending(false);
    }
  };

  const isAnswered = (thread: SupportThread) => thread.status === 'answered' || thread.status === 'waiting-driver';
  const statusLabel = (status: string) => (status === 'answered' ? 'Cevaplandi' : status === 'waiting-driver' ? 'Cevap bekleniyor' : 'Acik');
  const unansweredThreads = threads.filter((thread) => !isAnswered(thread));
  const answeredThreads = threads.filter((thread) => isAnswered(thread));
  const visibleThreads = activeTab === 'unanswered' ? unansweredThreads : answeredThreads;

  return (
    <div className="p-6 h-[calc(100vh-7rem)] flex flex-col">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Destek Merkezi</h1>
          <p className="text-sm text-gray-600 mt-1">Mobil suruculerden gelen yardim ve destek mesajlarini yonetin</p>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-6 min-h-0">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden min-h-0 flex flex-col">
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-slate-900">Talepler</h2>
              <span className="text-xs text-slate-500">{threads.length} kayit</span>
            </div>
            <div className="flex rounded-xl bg-slate-100 p-1 gap-1">
              <button
                type="button"
                onClick={() => setActiveTab('unanswered')}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${activeTab === 'unanswered' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Cevaplanmamis
                <span className={`ml-1 rounded-full px-1.5 py-0.5 ${activeTab === 'unanswered' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`}>{unansweredThreads.length}</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('answered')}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-semibold transition-colors ${activeTab === 'answered' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              >
                Cevaplanmis
                <span className={`ml-1 rounded-full px-1.5 py-0.5 ${activeTab === 'answered' ? 'bg-blue-100 text-blue-700' : 'bg-slate-200 text-slate-600'}`}>{answeredThreads.length}</span>
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading && <div className="p-4 text-sm text-slate-500">Talepler yukleniyor...</div>}
            {!loading && visibleThreads.length === 0 && (
              <div className="p-8 text-center">
                <LifeBuoy className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">{activeTab === 'unanswered' ? 'Cevaplanmamis destek kaydi bulunmuyor' : 'Cevaplanmis destek kaydi bulunmuyor'}</p>
              </div>
            )}
            {visibleThreads.map((thread) => (
              <button
                key={thread.id}
                type="button"
                onClick={() => selectThread(thread)}
                className={`w-full text-left px-4 py-4 border-b border-slate-100 transition-colors ${selectedThread?.id === thread.id ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium text-slate-900 truncate">{thread.driverName}</p>
                  <span className={`text-[11px] px-2 py-1 rounded-full ${thread.status === 'answered' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{statusLabel(thread.status)}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{thread.driverPhone || '-'}</p>
                <p className="text-sm text-slate-700 mt-2 line-clamp-2">{thread.lastMessage || thread.subject}</p>
                <p className="text-[11px] text-slate-400 mt-2">{new Date(thread.lastMessageAt).toLocaleString('tr-TR')}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm min-h-0 flex flex-col overflow-hidden">
          {!selectedThread ? (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div>
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">Mesajlari gormek icin soldan bir kayit secin</p>
              </div>
            </div>
          ) : (
            <>
              <div className="px-5 py-4 border-b border-slate-100">
                <p className="text-lg font-semibold text-slate-900">{selectedThread.driverName}</p>
                <p className="text-sm text-slate-500 mt-1">{selectedThread.driverPhone || 'Telefon bilgisi yok'}</p>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-slate-50">
                {messages.map((item) => {
                  const isDriver = item.senderRole === 'external-driver' || item.senderRole === 'driver' || item.senderRole === 'internal-driver';
                  return (
                    <div key={item.id} className={`flex ${isDriver ? 'justify-start' : 'justify-end'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${isDriver ? 'border border-slate-200 bg-white text-slate-800' : 'border border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-500/30 dark:bg-blue-600 dark:text-white'}`}>
                        <div className={`mb-1 text-[11px] ${isDriver ? 'text-slate-500' : 'text-blue-700 dark:text-blue-100'}`}>
                          {isDriver ? item.senderName || selectedThread.driverName : 'Destek Ekibi'}
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{item.message}</p>
                        <div className={`mt-2 text-[11px] ${isDriver ? 'text-slate-400' : 'text-blue-700 dark:text-blue-100'}`}>
                          {new Date(item.createdAt).toLocaleString('tr-TR')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="p-5 border-t border-slate-100 bg-white">
                <textarea
                  rows={4}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Surucuye iletilecek yaniti yazin..."
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <div className="mt-3 flex items-center justify-end">
                  <button
                    type="button"
                    onClick={handleReply}
                    disabled={!draft.trim() || sending}
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-slate-300"
                  >
                    <SendHorizonal className="w-4 h-4" />
                    {sending ? 'Gonderiliyor' : 'Yaniti Gonder'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
